import { memo, useEffect, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { sellerService } from "@/features/sellers/services/seller.service";
import type { SellerResponseDTO } from "@/features/sellers/types/seller.types";
import { Label } from "@/shared/components/ui/label";
import { Spinner } from "@/shared/components/ui/spinner";

interface SellerSelectFieldProps {
  value: number | null;
  /** Emite o id e o nome do vendedor — o nome alimenta o resumo do wizard. */
  onChange: (sellerId: number | null, sellerName: string | null) => void;
  error?: string;
}

/**
 * Select de vendedor no escopo USER.
 *
 * Carrega os primeiros 200 sellers via `sellerService.searchUser` no
 * mount — endpoint admin (`/admin/sellers/...`) retorna 403 para o role
 * USER. O limite de 200 cobre o volume atual; se crescer, migrar para
 * busca paginada com debounce.
 *
 * @example
 * <SellerSelectField
 *   value={form.watch('sellerId')}
 *   onChange={(id) => form.setValue('sellerId', id)}
 *   error={errors.sellerId?.message}
 * />
 */
export const SellerSelectField = memo(function SellerSelectField({
  value,
  onChange,
  error,
}: SellerSelectFieldProps) {
  const [sellers, setSellers] = useState<SellerResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    sellerService
      .searchUser({ size: 200 })
      .then((response) => {
        if (cancelled) return;
        setSellers(response.content);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        toast.error(getApiErrorMessage(error, "Erro ao carregar vendedores"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-2">
      <Label htmlFor="seller-select">Vendedor *</Label>
      <div className="relative">
        <select
          id="seller-select"
          value={value ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) {
              onChange(null, null);
              return;
            }
            const id = Number(v);
            const seller = sellers.find((s) => s.id === id);
            onChange(id, seller?.name ?? null);
          }}
          disabled={loading}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">Selecione um vendedor</option>
          {sellers.map((seller) => (
            <option key={seller.id} value={seller.id}>
              {seller.name}
            </option>
          ))}
        </select>
        {loading ? (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Spinner className="w-4 h-4" />
          </div>
        ) : null}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
});
